import DatePicker, { DatePickerProps } from "react-datepicker";
import { useMemo } from "react";
import { Form } from "react-bootstrap";
import { PatternFormat } from "react-number-format";

export type ControlledDateProps = DatePickerProps & {
    label?: string;
    inlineLabel?: boolean;
    withTime?: boolean;
    onlyTime?: boolean;
    inGroup?: boolean;
    ariaLabel?: string;
};

export function ControlledDateInput({
    label,
    inlineLabel,
    readOnly,
    className,
    withTime,
    onlyTime,
    dateFormat,
    timeFormat,
    inGroup,
    ariaLabel,
    value,
    onChange,
    isClearable,
    ...rest
}: Readonly<ControlledDateProps>) {
    const format = useMemo<string | string[] | undefined>(() => {
        if (dateFormat) return dateFormat;
        if (withTime) return "MM/dd/yyyy, HH:mm";
        if (onlyTime) return "HH:mm";
        return "MM/dd/yyyy";
    }, [dateFormat, onlyTime, withTime]);

    const mask = useMemo(() => {
        if (format === "MM/dd/yyyy") return "##/##/####";
        if (format === "HH:mm") return "##:##";
        if (format === "MM/dd/yyyy, HH:mm") return "##/##/####, ##:##";
        return undefined;
    }, [format]);

    const customInput = useMemo(() => {
        if (!mask) return undefined;

        return <PatternFormat format={mask} mask="_" className="form-control" />;
    }, [mask]);

    if (!inGroup)
        return (
            <Form.Group className={className}>
                <div className={inlineLabel ? "d-flex align-items-center" : ""}>
                    {!!label && (
                        <div>
                            <Form.Label style={{ whiteSpace: "nowrap" }} className={inlineLabel ? "me-2" : ""}>
                                {label}
                            </Form.Label>
                        </div>
                    )}

                    <div className="d-flex align-items-center">
                        <DatePicker
                            selected={value ? new Date(value) : undefined}
                            onChange={(...args: any[]) => onChange?.(args[0])}
                            readOnly={readOnly}
                            aria-readonly={readOnly}
                            aria-label={ariaLabel ?? label}
                            showTimeSelect={withTime ?? onlyTime}
                            showTimeSelectOnly={onlyTime}
                            dateFormat={format}
                            timeFormat={(timeFormat ?? (withTime || onlyTime)) ? "HH:mm" : undefined}
                            {...rest}
                            className="form-control"
                            timeIntervals={1}
                            customInput={customInput}
                            isClearable={isClearable ?? !readOnly}
                        />
                    </div>
                </div>
            </Form.Group>
        );

    return (
        <div className="d-flex align-items-center">
            <DatePicker
                selected={value ? new Date(value) : undefined}
                onChange={(...args: any[]) => onChange?.(args[0])}
                readOnly={readOnly}
                aria-readonly={readOnly}
                aria-label={ariaLabel ?? label}
                showTimeSelect={withTime ?? onlyTime}
                showTimeSelectOnly={onlyTime}
                dateFormat={format}
                timeFormat={(timeFormat ?? (withTime || onlyTime)) ? "HH:mm" : undefined}
                {...rest}
                className={`form-control ${className || ""}`}
                timeIntervals={1}
                customInput={customInput}
                isClearable={isClearable ?? !readOnly}
            />
        </div>
    );
}
